import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ChatBubbleOvalLeftIcon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  AcademicCapIcon,
  RocketLaunchIcon,
  BellIcon,
  UserIcon,
  MagnifyingGlassIcon,
  SunIcon,
  MoonIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const Header = () => {
  const navigate = useNavigate();

  // State management
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [showQuickLinksDropdown, setShowQuickLinksDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  
  // Mobile menu collapsible sections
  const [mobileCreateExpanded, setMobileCreateExpanded] = useState(false);
  const [mobileQuickLinksExpanded, setMobileQuickLinksExpanded] = useState(false);
  const [showAllQuickLinks, setShowAllQuickLinks] = useState(false);

  // Refs for dropdown management
  const createDropdownRef = useRef(null);
  const quickLinksRef = useRef(null);
  const userDropdownRef = useRef(null);
  const notificationsRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        createDropdownRef.current &&
        !createDropdownRef.current.contains(event.target)
      ) {
        setShowCreateDropdown(false);
      }
      if (
        quickLinksRef.current &&
        !quickLinksRef.current.contains(event.target)
      ) {
        setShowQuickLinksDropdown(false);
      }
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setShowUserDropdown(false);
      }
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest('[data-mobile-menu-button]')
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowSearchPopup(false);
        setIsMobileMenuOpen(false);
        setShowCreateDropdown(false);
        setShowQuickLinksDropdown(false);
        setShowUserDropdown(false);
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Handle body scroll lock for mobile menu
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      // Reset mobile collapse states when menu closes
      setMobileCreateExpanded(false);
      setMobileQuickLinksExpanded(false);
      setShowAllQuickLinks(false);
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Internal navigation shortcuts
  const internalLinks = [
    { id: 1, name: "Events", href: "/events", icon: "📅", description: "View all events and activities" },
    { id: 2, name: "Directory", href: "/directory", icon: "👥", description: "Employee directory and profiles" },
    { id: 3, name: "Forums", href: "/forums", icon: "💬", description: "Community discussions" },
    { id: 4, name: "Tasks", href: "/tasks", icon: "✅", description: "Manage your tasks and projects" },
    { id: 5, name: "Documents", href: "/documents", icon: "📄", description: "Document library and file management" },
    { id: 6, name: "Forms", href: "/forms", icon: "📝", description: "Fill out forms and track submissions" },
    { id: 7, name: "Notifications", href: "/notifications", icon: "🔔", description: "View all notifications and alerts" },
    { id: 8, name: "Calendar", href: "/calendar", icon: "📅", description: "Schedule and manage appointments" },
    { id: 9, name: "Attendance", href: "/attendance", icon: "📊", description: "Track your attendance and hours" },
    { id: 10, name: "Payroll", href: "/payroll", icon: "💳", description: "Salary, benefits and tax documents" },
    { id: 11, name: "Training", href: "/training", icon: "🎓", description: "Professional development and courses" },
    { id: 12, name: "Profile", href: "/profile", icon: "👤", description: "Manage your profile and settings" },
    { id: 13, name: "Monthly Attendance", href: "/attendance/monthly", icon: "📅", description: "Calendar view of attendance" },
    { id: 14, name: "Leave Balance", href: "/attendance/leave-balance", icon: "🏖️", description: "Manage your leave balance" },
    { id: 15, name: "Team Attendance", href: "/attendance/team", icon: "👥", description: "View team attendance status" },
    { id: 16, name: "Popular Discussions", href: "/forums/popular", icon: "🔥", description: "Trending topics" },
    { id: 17, name: "My Events", href: "/events/my-events", icon: "✨", description: "Your personal events" },
    { id: 18, name: "Event Categories", href: "/events/categories", icon: "🏷️", description: "Browse events by category" },
    { id: 19, name: "Department Directory", href: "/directory/departments", icon: "🏢", description: "Browse by departments" },
    { id: 20, name: "Team Directory", href: "/directory/teams", icon: "👨‍👩‍👧‍👦", description: "Team structures and hierarchy" },
    { id: 21, name: "Benefits Overview", href: "/payroll/benefits", icon: "🛡️", description: "View your employee benefits" },
    { id: 22, name: "Tax Documents", href: "/payroll/tax-documents", icon: "📄", description: "Download Form 16 and TDS certificates" },
    { id: 23, name: "Payroll History", href: "/payroll/history", icon: "📈", description: "View salary history and trends" },
    { id: 24, name: "Course Catalog", href: "/training/catalog", icon: "📚", description: "Browse available training courses" },
    { id: 25, name: "My Training", href: "/training/my-training", icon: "📖", description: "Track your learning progress" },
    { id: 26, name: "Skill Assessments", href: "/training/assessments", icon: "🎯", description: "Test your skills and knowledge" },
    { id: 27, name: "Certificates", href: "/training/certificates", icon: "🏆", description: "View your earned certificates" },
    { id: 28, name: "Support Center", href: "/support", icon: "🆘", description: "Get help and support" },
    { id: 29, name: "Support Tickets", href: "/support/tickets", icon: "🎫", description: "View and manage support tickets" },
    { id: 30, name: "Knowledge Base", href: "/support/knowledge-base", icon: "📖", description: "Browse help articles and FAQs" },
    { id: 31, name: "Contact Support", href: "/support/contact", icon: "📞", description: "Get in touch with support team" },
    { id: 32, name: "Live Chat", href: "/support/chat", icon: "💬", description: "Chat with support agents" },
  ];


  return (
    <header className="bg-white shadow-lg border-b border-apollo-light sticky top-0 z-50 backdrop-blur-md bg-white/95">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-18">
          {/* Logo Section */}
          <div className="flex items-center space-x-2">
            <img
              src="https://www.apollohospitals.com/themes/custom/apollo/assets/svg/apollo-logo.svg"
              alt="Apollo Hospitals"
              className="h-8 lg:h-10 w-auto cursor-pointer"
              onClick={() => navigate("/")}
            />
            <div className="border-l border-gray-300 pl-3 hidden sm:block">
              <div className="text-[16px] lg:text-[18px] font-bold text-apollo-primary">
                Apollo Noida
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {/* Navigation Menu */}
            <nav className="flex space-x-2 text-[14px]">
              {/* Create Dropdown */}
              <div className="relative" ref={createDropdownRef}>
                <button
                  onClick={() => setShowCreateDropdown(!showCreateDropdown)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-apollo-accent font-medium transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-gray-50"
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>Create</span>
                  <ChevronDownIcon
                    className={`w-4 h-4 transition-transform duration-300 ${
                      showCreateDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Create Dropdown Menu */}
                {showCreateDropdown && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                    <div className="bg-apollo-accent/5 p-3 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-apollo-primary">
                        Create New
                      </h3>
                      <p className="text-xs text-gray-600">
                        Quick content creation tools
                      </p>
                    </div>

                    <div className="p-2">
                      <div className="space-y-1">
                        <button 
                          onClick={() => {
                            navigate("/documents");
                            setShowCreateDropdown(false);
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-apollo-accent/10 rounded-lg transition-colors text-left"
                        >
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <DocumentTextIcon className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium">Document</div>
                            <div className="text-xs text-gray-500">
                              Create a new document
                            </div>
                          </div>
                        </button>

                        <button 
                          onClick={() => {
                            navigate("/forms");
                            setShowCreateDropdown(false);
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-apollo-accent/10 rounded-lg transition-colors text-left"
                        >
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <ClipboardDocumentListIcon className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium">Form</div>
                            <div className="text-xs text-gray-500">
                              Create a new form
                            </div>
                          </div>
                        </button>

                        <button 
                          onClick={() => {
                            navigate("/forums");
                            setShowCreateDropdown(false);
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-apollo-accent/10 rounded-lg transition-colors text-left"
                        >
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                            <ChatBubbleOvalLeftIcon className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium">Forum</div>
                            <div className="text-xs text-gray-500">
                              Start a discussion
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={() => {
                            navigate("/events");
                            setShowCreateDropdown(false);
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-apollo-accent/10 rounded-lg transition-colors text-left"
                        >
                          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                            <CalendarDaysIcon className="w-4 h-4 text-amber-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium">Event</div>
                            <div className="text-xs text-gray-500">
                              Schedule an event
                            </div>
                          </div>
                        </button>

                        <button 
                          onClick={() => {
                            navigate("/tasks");
                            setShowCreateDropdown(false);
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-apollo-accent/10 rounded-lg transition-colors text-left"
                        >
                          <div className="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center">
                            <ClipboardDocumentCheckIcon className="w-4 h-4 text-cyan-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium">Task</div>
                            <div className="text-xs text-gray-500">
                              Create a new task
                            </div>
                          </div>
                        </button>

                        <button 
                          onClick={() => {
                            navigate("/calendar");
                            setShowCreateDropdown(false);
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-apollo-accent/10 rounded-lg transition-colors text-left"
                        >
                          <div className="w-8 h-8 bg-rose-100 rounded-lg flex items-center justify-center">
                            <CalendarDaysIcon className="w-4 h-4 text-rose-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium">Meeting</div>
                            <div className="text-xs text-gray-500">
                              Schedule a meeting
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Main Menu Items */}

              <button 
                onClick={() => navigate("/support")}
                className="flex items-center space-x-2 text-gray-700 hover:text-apollo-accent font-medium transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-gray-50"
              >
                <Cog6ToothIcon className="w-4 h-4" />
                <span>Service Desk</span>
              </button>

              <button 
                onClick={() => navigate("/forums")}
                className="flex items-center space-x-2 text-gray-700 hover:text-apollo-accent font-medium transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-gray-50"
              >
                <ChatBubbleOvalLeftIcon className="w-4 h-4" />
                <span>Forums</span>
              </button>

              <button
                onClick={() => navigate("/directory")}
                className="flex items-center space-x-2 text-gray-700 hover:text-apollo-accent font-medium transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-gray-50"
              >
                <UserGroupIcon className="w-4 h-4" />
                <span>Directory</span>
              </button>

              <button
                onClick={() => navigate("/support")}
                className="flex items-center space-x-2 text-gray-700 hover:text-apollo-accent font-medium transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-gray-50"
              >
                <AcademicCapIcon className="w-4 h-4" />
                <span>Wiki</span>
              </button>

              {/* Quick Links Dropdown */}
              <div className="relative" ref={quickLinksRef}>
                <button
                  onClick={() =>
                    setShowQuickLinksDropdown(!showQuickLinksDropdown)
                  }
                  className="flex items-center space-x-2 text-gray-700 hover:text-apollo-accent font-medium transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-gray-50"
                >
                  <RocketLaunchIcon className="w-4 h-4" />
                  <span>Quick Links</span>
                  <ChevronDownIcon
                    className={`w-4 h-4 transition-transform duration-300 ${
                      showQuickLinksDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Quick Links Dropdown Menu */}
                {showQuickLinksDropdown && (
                  <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                    <div className="bg-apollo-accent/5 p-3 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-apollo-primary">
                        Quick Access Links
                      </h3>
                      <p className="text-xs text-gray-600">
                        Navigate to key sections quickly
                      </p>
                    </div>

                    <div className="max-h-80 overflow-y-auto">
                      <div className="p-2">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">
                          Internal Navigation
                        </h4>
                        <div className="space-y-1">
                          {internalLinks.map((link) => (
                            <button
                              key={link.id}
                              onClick={() => {
                                navigate(link.href);
                                setShowQuickLinksDropdown(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-apollo-accent/10 rounded-lg transition-colors text-left"
                            >
                              <div className="w-8 h-8 bg-apollo-accent/10 rounded-lg flex items-center justify-center">
                                <span className="text-sm">{link.icon}</span>
                              </div>
                              <div className="flex-1">
                                <div className="text-sm font-medium">
                                  {link.name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {link.description}
                                </div>
                              </div>
                              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </nav>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2 lg:space-x-4">
            {/* Search Icon - Opens popup */}
            <button
              onClick={() => setShowSearchPopup(true)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <MagnifyingGlassIcon className="w-5 h-5 text-gray-600" />
            </button>

            {/* Dark Mode Toggle - Hidden on mobile */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors hidden md:block"
            >
              {isDarkMode ? (
                <SunIcon className="w-5 h-5 text-gray-600" />
              ) : (
                <MoonIcon className="w-5 h-5 text-gray-600" />
              )}
            </button>

            {/* Notifications */}
            <div className="relative" ref={notificationsRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors relative"
              >
                <BellIcon className="w-5 h-5 text-gray-600" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">
                  3
                </span>
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-gray-200 z-50 right-0 transform -translate-x-16 sm:transform-none sm:right-0">
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      Notifications
                    </h3>
                    <div className="space-y-2">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <p className="text-sm text-gray-900">
                          New system update available
                        </p>
                        <p className="text-xs text-gray-500">2 minutes ago</p>
                      </div>
                      <div className="p-2 hover:bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-900">
                          Meeting reminder: Team sync
                        </p>
                        <p className="text-xs text-gray-500">15 minutes ago</p>
                      </div>
                      <div className="p-2 hover:bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-900">
                          Document shared with you
                        </p>
                        <p className="text-xs text-gray-500">1 hour ago</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* User Profile */}
            <div className="relative" ref={userDropdownRef}>
              <button
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 bg-apollo-accent rounded-full flex items-center justify-center">
                  <UserIcon className="w-5 h-5 text-white" />
                </div>
                <ChevronDownIcon className="w-4 h-4 text-gray-600" />
              </button>

              {/* User Dropdown */}
              {showUserDropdown && (
                <div className="absolute top-full mt-2 w-64 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-gray-200 z-50 right-0 transform -translate-x-8 sm:transform-none sm:right-0">
                  <div className="p-4 border-b border-gray-200">
                    <p className="font-semibold text-gray-900">
                      Dr. Sarah Johnson
                    </p>
                    <p className="text-sm text-gray-600">
                      sarah.johnson@apollo.com
                    </p>
                  </div>
                  <div className="p-2">
                    <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                      Profile Settings
                    </button>
                    <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                      Preferences
                    </button>
                    <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                      Help & Support
                    </button>
                    <hr className="my-2" />
                    <button 
                      onClick={() => {
                        localStorage.removeItem("isAuthenticated");
                        window.location.href = "/login";
                      }}
                      className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden ml-4">
              <button
                data-mobile-menu-button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="w-6 h-6" />
                ) : (
                  <Bars3Icon className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>


        </div>
      </div>

      {/* Mobile menu backdrop */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-25 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div 
          ref={mobileMenuRef}
          className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-lg border-b border-gray-200 z-40 max-h-screen overflow-y-auto"
        >
          <div className="max-w-7xl mx-auto px-4 py-4">
            {/* Mobile Search with Close Button */}
            <div className="mb-4 flex items-center gap-2">
              <button
                onClick={() => {
                  setShowSearchPopup(true);
                  setIsMobileMenuOpen(false);
                }}
                className="flex-1 flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <MagnifyingGlassIcon className="w-5 h-5" />
                <span>Search Portal</span>
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-3 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Mobile Create Section */}
            <div className="mb-4 border-b border-gray-200 pb-4">
              <button
                onClick={() => setMobileCreateExpanded(!mobileCreateExpanded)}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Create New</h3>
                <ChevronDownIcon
                  className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                    mobileCreateExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>
              
              {mobileCreateExpanded && (
                <div className="space-y-1 mt-2 animate-in slide-in-from-top-2 duration-200">
                  <button 
                    onClick={() => {
                      navigate("/documents");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                    <span>Document</span>
                  </button>
                  <button 
                    onClick={() => {
                      navigate("/forms");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ClipboardDocumentListIcon className="w-5 h-5 text-green-600" />
                    <span>Form</span>
                  </button>
                  <button 
                    onClick={() => {
                      navigate("/forums");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChatBubbleOvalLeftIcon className="w-5 h-5 text-purple-600" />
                    <span>Forum Discussion</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate("/events");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <CalendarDaysIcon className="w-5 h-5 text-amber-600" />
                    <span>Event</span>
                  </button>
                  <button 
                    onClick={() => {
                      navigate("/tasks");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ClipboardDocumentCheckIcon className="w-5 h-5 text-cyan-600" />
                    <span>Task</span>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Main Navigation */}
            <div className="mb-4 border-b border-gray-200 pb-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide px-4 mb-2">Main Menu</h3>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    navigate("/support");
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Cog6ToothIcon className="w-5 h-5" />
                  <span>Service Desk</span>
                </button>

                <button
                  onClick={() => {
                    navigate("/forums");
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChatBubbleOvalLeftIcon className="w-5 h-5" />
                  <span>Forums</span>
                </button>

                <button
                  onClick={() => {
                    navigate("/directory");
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <UserGroupIcon className="w-5 h-5" />
                  <span>Directory</span>
                </button>

                <button
                  onClick={() => {
                    navigate("/support");
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <AcademicCapIcon className="w-5 h-5" />
                  <span>Wiki</span>
                </button>
              </div>
            </div>

            {/* Mobile Quick Links */}
            <div className="mb-4 border-b border-gray-200 pb-4">
              <button
                onClick={() => setMobileQuickLinksExpanded(!mobileQuickLinksExpanded)}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Quick Links</h3>
                <ChevronDownIcon
                  className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                    mobileQuickLinksExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>
              
              {mobileQuickLinksExpanded && (
                <div className="space-y-1 max-h-60 overflow-y-auto mt-2 animate-in slide-in-from-top-2 duration-200">
                  {(showAllQuickLinks ? internalLinks : internalLinks.slice(0, 8)).map((link) => (
                    <button
                      key={link.id}
                      onClick={() => {
                        navigate(link.href);
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <span className="text-sm">{link.icon}</span>
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-sm font-medium">{link.name}</div>
                        <div className="text-xs text-gray-500 truncate">{link.description}</div>
                      </div>
                    </button>
                  ))}
                  {!showAllQuickLinks && (
                    <button
                      onClick={() => setShowAllQuickLinks(true)}
                      className="w-full px-4 py-2 text-apollo-accent hover:bg-apollo-accent/10 rounded-lg transition-colors text-center text-sm font-medium"
                    >
                      View All Quick Links ({internalLinks.length - 8} more)
                    </button>
                  )}
                  {showAllQuickLinks && (
                    <button
                      onClick={() => setShowAllQuickLinks(false)}
                      className="w-full px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors text-center text-sm font-medium"
                    >
                      Show Less
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Mobile Settings */}
            <div className="space-y-1">
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {isDarkMode ? (
                  <SunIcon className="w-5 h-5" />
                ) : (
                  <MoonIcon className="w-5 h-5" />
                )}
                <span>{isDarkMode ? "Light Mode" : "Dark Mode"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Popup Modal */}
      {showSearchPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-start justify-center pt-20">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
            {/* Search Header */}
            <div className="bg-gradient-to-r from-apollo-accent to-blue-600 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-semibold text-lg">Search Apollo Portal</h3>
                <button
                  onClick={() => setShowSearchPopup(false)}
                  className="text-white hover:bg-white/20 p-1 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Search Input */}
            <div className="p-6">
              <div className="relative mb-6">
                <input
                  type="text"
                  placeholder="Search for employees, departments, events, documents..."
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent text-lg outline-none transition-all duration-200"
                  autoFocus
                />
                <MagnifyingGlassIcon className="w-6 h-6 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
              </div>

              {/* Quick Search Categories */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Quick Search</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <button 
                    onClick={() => {
                      setShowSearchPopup(false);
                      navigate('/directory');
                    }}
                    className="flex flex-col items-center p-4 bg-gray-50 hover:bg-apollo-accent/10 rounded-xl transition-colors group"
                  >
                    <UserGroupIcon className="w-8 h-8 text-apollo-accent mb-2 group-hover:scale-110 transition-transform" />
                    <span className="text-sm font-medium text-gray-700">Employees</span>
                  </button>
                  
                  <button 
                    onClick={() => {
                      setShowSearchPopup(false);
                      navigate('/events');
                    }}
                    className="flex flex-col items-center p-4 bg-gray-50 hover:bg-apollo-accent/10 rounded-xl transition-colors group"
                  >
                    <CalendarDaysIcon className="w-8 h-8 text-apollo-accent mb-2 group-hover:scale-110 transition-transform" />
                    <span className="text-sm font-medium text-gray-700">Events</span>
                  </button>
                  
                  <button className="flex flex-col items-center p-4 bg-gray-50 hover:bg-apollo-accent/10 rounded-xl transition-colors group">
                    <DocumentTextIcon className="w-8 h-8 text-apollo-accent mb-2 group-hover:scale-110 transition-transform" />
                    <span className="text-sm font-medium text-gray-700">Documents</span>
                  </button>
                  
                  <button className="flex flex-col items-center p-4 bg-gray-50 hover:bg-apollo-accent/10 rounded-xl transition-colors group">
                    <ChatBubbleOvalLeftIcon className="w-8 h-8 text-apollo-accent mb-2 group-hover:scale-110 transition-transform" />
                    <span className="text-sm font-medium text-gray-700">Forums</span>
                  </button>
                </div>
              </div>

              {/* Recent Searches */}
              <div className="mt-6 space-y-3">
                <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Recent Searches</h4>
                <div className="space-y-2">
                  <button className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors flex items-center space-x-3">
                    <div className="w-8 h-8 bg-apollo-accent/10 rounded-lg flex items-center justify-center">
                      <MagnifyingGlassIcon className="w-4 h-4 text-apollo-accent" />
                    </div>
                    <span className="text-gray-700">Dr. Anita Reddy</span>
                  </button>
                  <button className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors flex items-center space-x-3">
                    <div className="w-8 h-8 bg-apollo-accent/10 rounded-lg flex items-center justify-center">
                      <MagnifyingGlassIcon className="w-4 h-4 text-apollo-accent" />
                    </div>
                    <span className="text-gray-700">Cardiology Department</span>
                  </button>
                  <button className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors flex items-center space-x-3">
                    <div className="w-8 h-8 bg-apollo-accent/10 rounded-lg flex items-center justify-center">
                      <MagnifyingGlassIcon className="w-4 h-4 text-apollo-accent" />
                    </div>
                    <span className="text-gray-700">Medical Conference 2025</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
